/**
 * Утилиты для валидации данных
 */

import { LIMITS, SUPPORTED_FILE_TYPES, SUPPORTED_FILE_EXTENSIONS } from '@/constants/defaults';
import { MessageFileType } from '@/types/message.types';

/**
 * Валидирует текст сообщения
 * @param text - текст для валидации
 * @returns объект с результатом валидации
 */
export function validateMessage(text: string): {
  valid: boolean;
  error?: string;
} {
  // Проверка на пустоту
  if (!text || !text.trim()) {
    return {
      valid: false,
      error: 'error_empty_message',
    };
  }

  // Проверка длины
  if (text.length > LIMITS.MAX_MESSAGE_LENGTH) {
    return {
      valid: false,
      error: `Сообщение слишком длинное (макс ${LIMITS.MAX_MESSAGE_LENGTH} символов)`,
    };
  }

  return { valid: true };
}

/**
 * Валидирует файл для загрузки
 * @param file - файл для валидации
 * @returns объект с результатом валидации
 */
export function validateFile(file: File): {
  valid: boolean;
  error?: string;
  fileType?: MessageFileType;
} {
  // Проверка размера
  if (file.size > LIMITS.MAX_FILE_SIZE) {
    return {
      valid: false,
      error: 'error_file_too_large',
    };
  }

  // Определение типа файла
  const fileType = getFileType(file);

  if (!fileType) {
    return {
      valid: false,
      error: 'error_unsupported_file',
    };
  }

  return {
    valid: true,
    fileType,
  };
}

/**
 * Определяет тип файла по MIME типу
 * @param file - файл
 * @returns тип файла или null
 */
export function getFileType(file: File): MessageFileType | null {
  const mimeType = file.type.toLowerCase();
  const extension = file.name.split('.').pop()?.toLowerCase();

  // Проверка по MIME типу
  if ((SUPPORTED_FILE_TYPES.image as readonly string[]).includes(mimeType)) {
    return 'image';
  }
  if ((SUPPORTED_FILE_TYPES.document as readonly string[]).includes(mimeType)) {
    return 'document';
  }
  if ((SUPPORTED_FILE_TYPES.audio as readonly string[]).includes(mimeType)) {
    return 'audio';
  }
  if ((SUPPORTED_FILE_TYPES.video as readonly string[]).includes(mimeType)) {
    return 'video';
  }

  // Fallback: проверка по расширению
  if (extension) {
    const ext = `.${extension}`;
    if ((SUPPORTED_FILE_EXTENSIONS.image as readonly string[]).includes(ext)) {
      return 'image';
    }
    if ((SUPPORTED_FILE_EXTENSIONS.document as readonly string[]).includes(ext)) {
      return 'document';
    }
    if ((SUPPORTED_FILE_EXTENSIONS.audio as readonly string[]).includes(ext)) {
      return 'audio';
    }
    if ((SUPPORTED_FILE_EXTENSIONS.video as readonly string[]).includes(ext)) {
      return 'video';
    }
  }

  return null;
}

/**
 * Форматирует размер файла в читаемый вид
 * @param bytes - размер в байтах
 * @returns строка с размером (например "1.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

